import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Upload, Loader2, CheckCircle2, FileText, Building2, Phone, Briefcase, Share2 } from 'lucide-react';
import { RegistrationWizard, ChipMultiSelect, ChipInput, WizardStep } from '@/components/onboarding/RegistrationWizard';

interface DocUpload { label: string; field: string; required: boolean; description?: string; group: string; }

const allDocs: DocUpload[] = [
  { group: 'Legal', label: 'Business Registration Certificate', field: 'business_registration_url', required: true },
  { group: 'Legal', label: 'Commercial (Trade) License', field: 'trade_license_url', required: true, description: 'Renewed and valid' },
  { group: 'Legal', label: 'TIN Certificate', field: 'tin_certificate_url', required: true },
  { group: 'Legal', label: 'VAT Registration Certificate', field: 'vat_certificate_url', required: false, description: 'If applicable' },
  { group: 'Credentials', label: 'Real Estate Developer License', field: 'developer_license_url', required: false },
  { group: 'Credentials', label: 'Letter of Active Involvement', field: 'involvement_letter_url', required: false },
  { group: 'Identification', label: 'ID or Passport of Owner(s)', field: 'id_document_url', required: true },
  { group: 'Identification', label: 'Power of Attorney', field: 'power_of_attorney_url', required: false },
  { group: 'Membership', label: 'Proof of Registration Fee Payment', field: 'membership_fee_proof_url', required: true },
  { group: 'Optional', label: 'Recommendation Letter', field: 'recommendation_letter_url', required: false },
  { group: 'Optional', label: 'Financial Statements', field: 'financial_statement_url', required: false },
  { group: 'Optional', label: 'Tax Clearance Certificate', field: 'tax_clearance_url', required: false },
];

const SERVICE_OPTIONS = ['Residential Projects', 'Commercial Projects', 'Mixed-Use', 'Apartments', 'Villas', 'Land Development', 'Affordable Housing'];

export default function DeveloperApplication() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [website, setWebsite] = useState('');
  const [establishedYear, setEstablishedYear] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [description, setDescription] = useState('');
  const [services, setServices] = useState<string[]>([]);
  const [areas, setAreas] = useState<string[]>([]);
  const [socialFacebook, setSocialFacebook] = useState('');
  const [socialInstagram, setSocialInstagram] = useState('');
  const [socialLinkedin, setSocialLinkedin] = useState('');
  const [socialTelegram, setSocialTelegram] = useState('');

  const [docs, setDocs] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  if (!user) { navigate('/login'); return null; }

  const uploadDoc = async (file: File, field: string) => {
    if (!['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast({ variant: 'destructive', title: 'Invalid file' }); return;
    }
    if (file.size > 15 * 1024 * 1024) { toast({ variant: 'destructive', title: 'File too large', description: 'Max 15MB' }); return; }
    setUploading(p => ({ ...p, [field]: true }));
    const ext = file.name.split('.').pop() || 'pdf';
    const path = `${user.id}/${field}_${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('developer-documents').upload(path, file, { cacheControl: '3600' });
    if (error) toast({ variant: 'destructive', title: 'Upload failed', description: error.message });
    else setDocs(p => ({ ...p, [field]: path }));
    setUploading(p => ({ ...p, [field]: false }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const { data: insertedApp, error } = await supabase.from('developer_applications' as any).insert({
      user_id: user.id, company_name: companyName,
      phone, whatsapp: whatsapp || null, email,
      address: address || null, city: city || null, website: website || null,
      established_year: establishedYear ? Number(establishedYear) : null,
      employee_count: employeeCount ? Number(employeeCount) : null,
      description: description || null,
      services_offered: services, areas_of_operation: areas,
      social_facebook: socialFacebook || null, social_instagram: socialInstagram || null,
      social_linkedin: socialLinkedin || null, social_telegram: socialTelegram || null,
      ...docs,
    }).select('id').single();
    setSubmitting(false);

    if (error) { toast({ variant: 'destructive', title: 'Submission failed', description: error.message }); return; }

    const { data: reviewers } = await supabase.from('user_roles').select('user_id')
      .in('role', ['superadmin', 'sales_manager', 'sales_team_leader', 'sales_agent'] as any);
    if (reviewers?.length) {
      await supabase.from('notifications').insert(reviewers.map((r: any) => ({
        user_id: r.user_id,
        title: 'New developer application',
        message: `${companyName} submitted a developer application for review.`,
        type: 'application_submitted',
        metadata: { application_id: (insertedApp as any)?.id, application_type: 'developer' },
      })));
    }
    toast({ title: 'Application Submitted!', description: 'Our team will review your application.' });
    navigate('/dashboard');
  };

  const Field = ({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label} {required && <span className="text-destructive">*</span>}</Label>
      {children}
    </div>
  );

  const docGroups = ['Legal', 'Credentials', 'Identification', 'Membership', 'Optional'];

  const steps: WizardStep[] = [
    {
      id: 'basic', title: 'Company Profile', shortTitle: 'Profile', icon: <Building2 className="h-5 w-5 text-primary" />,
      validate: () => !companyName.trim() ? 'Company name is required' : null,
      render: () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Company Name" required><Input value={companyName} onChange={e => setCompanyName(e.target.value)} /></Field>
          <Field label="Established Year"><Input type="number" value={establishedYear} onChange={e => setEstablishedYear(e.target.value)} placeholder="2010" /></Field>
          <Field label="Number of Employees"><Input type="number" value={employeeCount} onChange={e => setEmployeeCount(e.target.value)} placeholder="50" /></Field>
          <Field label="City"><Input value={city} onChange={e => setCity(e.target.value)} placeholder="Addis Ababa" /></Field>
        </div>
      ),
    },
    {
      id: 'contact', title: 'Contact Information', shortTitle: 'Contact', icon: <Phone className="h-5 w-5 text-primary" />,
      validate: () => !phone.trim() ? 'Phone is required' : !email.trim() ? 'Email is required' : null,
      render: () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Phone" required><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+251..." /></Field>
          <Field label="WhatsApp"><Input value={whatsapp} onChange={e => setWhatsapp(e.target.value)} /></Field>
          <Field label="Email" required><Input type="email" value={email} onChange={e => setEmail(e.target.value)} /></Field>
          <Field label="Website"><Input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." /></Field>
          <Field label="Office Address"><Input value={address} onChange={e => setAddress(e.target.value)} /></Field>
        </div>
      ),
    },
    {
      id: 'about', title: 'Company Background', shortTitle: 'About', icon: <FileText className="h-5 w-5 text-primary" />,
      render: () => (
        <Field label="Description / Past Projects">
          <Textarea value={description} onChange={e => setDescription(e.target.value.slice(0, 1500))} rows={6}
            placeholder="Brief description of your company, past projects, expertise…" />
          <div className="text-xs text-muted-foreground text-right mt-1">{description.length}/1500</div>
        </Field>
      ),
    },
    {
      id: 'services', title: 'Services & Areas', shortTitle: 'Services', icon: <Briefcase className="h-5 w-5 text-primary" />,
      render: () => (
        <div className="space-y-6">
          <div>
            <Label className="text-xs font-medium">Specializations</Label>
            <p className="text-xs text-muted-foreground mb-3">Select all that apply</p>
            <ChipMultiSelect options={SERVICE_OPTIONS} value={services} onChange={setServices} />
          </div>
          <div>
            <Label className="text-xs font-medium">Areas of Operation</Label>
            <p className="text-xs text-muted-foreground mb-3">Cities / neighborhoods. Press Enter after each.</p>
            <ChipInput value={areas} onChange={setAreas} placeholder="e.g. Addis Ababa, Hawassa" />
          </div>
        </div>
      ),
    },
    {
      id: 'social', title: 'Social Media (Optional)', shortTitle: 'Social', icon: <Share2 className="h-5 w-5 text-primary" />,
      render: () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Facebook"><Input value={socialFacebook} onChange={e => setSocialFacebook(e.target.value)} placeholder="https://facebook.com/..." /></Field>
          <Field label="Instagram"><Input value={socialInstagram} onChange={e => setSocialInstagram(e.target.value)} placeholder="https://instagram.com/..." /></Field>
          <Field label="LinkedIn"><Input value={socialLinkedin} onChange={e => setSocialLinkedin(e.target.value)} placeholder="https://linkedin.com/..." /></Field>
          <Field label="Telegram"><Input value={socialTelegram} onChange={e => setSocialTelegram(e.target.value)} placeholder="https://t.me/..." /></Field>
        </div>
      ),
    },
    {
      id: 'docs', title: 'Verification Documents', shortTitle: 'Documents', icon: <FileText className="h-5 w-5 text-primary" />,
      validate: () => {
        const required = allDocs.filter(d => d.required).map(d => d.field);
        const missing = required.filter(f => !docs[f]);
        return missing.length > 0 ? `${missing.length} required document(s) remaining` : null;
      },
      render: () => (
        <div className="space-y-5">
          {docGroups.map(group => (
            <div key={group}>
              <h3 className="text-sm font-semibold mb-2 text-foreground/80">{group} Documents</h3>
              <div className="space-y-2">
                {allDocs.filter(d => d.group === group).map(doc => (
                  <div key={doc.field} className="flex items-center gap-3 p-3 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{doc.label} {doc.required && <span className="text-destructive">*</span>}</div>
                      {doc.description && <div className="text-xs text-muted-foreground">{doc.description}</div>}
                    </div>
                    {docs[doc.field] ? (
                      <div className="flex items-center gap-2 text-sm text-green-600"><CheckCircle2 className="h-4 w-4" />Uploaded</div>
                    ) : uploading[doc.field] ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : (
                      <label className="cursor-pointer">
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-medium hover:bg-primary/20"><Upload className="h-3.5 w-3.5" /> Upload</div>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) uploadDoc(f, doc.field); e.target.value = ''; }} />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'review', title: 'Review & Submit', shortTitle: 'Review', icon: <CheckCircle2 className="h-5 w-5 text-primary" />,
      render: () => (
        <div className="space-y-2 text-sm">
          <ReviewRow label="Company" value={companyName} />
          <ReviewRow label="Established" value={establishedYear} />
          <ReviewRow label="Employees" value={employeeCount} />
          <ReviewRow label="Phone" value={phone} />
          <ReviewRow label="WhatsApp" value={whatsapp} />
          <ReviewRow label="Email" value={email} />
          <ReviewRow label="City" value={city} />
          <ReviewRow label="Address" value={address} />
          <ReviewRow label="Website" value={website} />
          <ReviewRow label="Specializations" value={services.join(', ')} />
          <ReviewRow label="Areas" value={areas.join(', ')} />
          <ReviewRow label="Documents" value={`${Object.keys(docs).length} uploaded`} />
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <RegistrationWizard
        steps={steps}
        onSubmit={handleSubmit}
        submitting={submitting}
        title="Developer Registration"
        subtitle="Become a verified developer on Ethio Real Estates and showcase your projects."
        topIcon={<Building2 className="h-12 w-12 mx-auto text-primary" />}
      />
    </Layout>
  );
}

function ReviewRow({ label, value }: { label: string; value?: string | number | null }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between gap-4 py-2 border-b last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right break-words max-w-[60%]">{value}</span>
    </div>
  );
}
